import {
  LocalMatchday,
  CreateLocalMatchdayRequest,
  UpdateLocalMatchdayRequest,
  LocalMatch,
  CreateLocalMatchRequest,
  UpdateLocalMatchRequest,
  LocalMatchStatusInfo
} from '../types/localMatchday';
import { getToken } from '../utils/auth';
import { getPartnerToken } from '../utils/partnerAuth';

// Resolve API base: use absolute VITE_API_BASE when provided; otherwise current origin.
const _configured = (import.meta as any).env?.VITE_API_BASE as string | undefined;
const API_BASE_URL = _configured && /^https?:\/\//i.test(_configured) ? _configured : window.location.origin;

export class LocalMatchdayApi {
  private static async fetchPublic(url: string, options: RequestInit = {}): Promise<Response> {
    // Try to get club token first, then partner token
    let token = getToken();
    let tokenType = 'club';

    if (!token) {
      token = getPartnerToken();
      tokenType = 'partner';
    }

    if (!token) {
      console.error('No token found for authenticated request to:', url);
    } else {
      console.log(`Making authenticated request to: ${url} with ${tokenType} token`);
    }

    const headers = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    };

    const response = await fetch(`${API_BASE_URL}/api${url}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      if (response.status === 401) {
        console.error('Unauthorized! Token may be invalid or expired');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response;
  }

  // Local Matchday methods
  static async getAllLocalMatchdays(includeInactive: boolean = false): Promise<LocalMatchday[]> {
    const params = new URLSearchParams();
    if (includeInactive) {
      params.append('includeInactive', 'true');
    }

    const url = params.toString() ? `/LocalMatchday?${params.toString()}` : `/LocalMatchday`;
    const response = await this.fetchPublic(url);
    const data = await response.json();
    return data.data || [];
  }

  static async getLocalMatchdayById(id: number): Promise<LocalMatchday | null> {
    try {
      const response = await this.fetchPublic(`/LocalMatchday/${id}`);
      const data = await response.json();
      return data.data || null;
    } catch (error) {
      console.error('Error fetching local matchday:', error);
      return null;
    }
  }

  static async createLocalMatchday(request: CreateLocalMatchdayRequest): Promise<LocalMatchday> {
    const response = await this.fetchPublic(`/LocalMatchday`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
    const data = await response.json();
    return data.data;
  }

  static async updateLocalMatchday(id: number, request: UpdateLocalMatchdayRequest): Promise<LocalMatchday> {
    const response = await this.fetchPublic(`/LocalMatchday/${id}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    });
    const data = await response.json();
    return data.data;
  }

  static async deleteLocalMatchday(id: number): Promise<boolean> {
    try {
      await this.fetchPublic(`/LocalMatchday/${id}`, {
        method: 'DELETE',
      });
      return true;
    } catch (error) {
      console.error('Error deleting local matchday:', error);
      return false;
    }
  }

  static async toggleLocalMatchdayActive(id: number): Promise<LocalMatchday> {
    const response = await this.fetchPublic(`/LocalMatchday/${id}/toggle-active`, {
      method: 'PATCH',
    });
    const data = await response.json();
    return data.data;
  }

  static async canDeleteLocalMatchday(id: number): Promise<boolean> {
    try {
      const response = await this.fetchPublic(`/LocalMatchday/${id}/can-delete`);
      const data = await response.json();
      return data.data || false;
    } catch (error) {
      console.error('Error checking if local matchday can be deleted:', error);
      return false;
    }
  }
}

export class LocalMatchApi {
  private static async fetchPublic(url: string, options: RequestInit = {}): Promise<Response> {
    const token = getToken();

    const headers = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    };

    const response = await fetch(`${API_BASE_URL}/api${url}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      if (response.status === 401) {
        console.error('Unauthorized! Token may be invalid or expired');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response;
  }

  // Local Match methods
  static async getAllLocalMatches(includeInactive: boolean = false): Promise<LocalMatch[]> {
    const params = new URLSearchParams();
    if (includeInactive) {
      params.append('includeInactive', 'true');
    }

    const url = params.toString() ? `/LocalMatch?${params.toString()}` : `/LocalMatch`;
    const response = await this.fetchPublic(url);
    const data = await response.json();
    return data.data || [];
  }

  static async getLocalMatchesByMatchday(matchdayId: number): Promise<LocalMatch[]> {
    const response = await this.fetchPublic(`/LocalMatch/matchday/${matchdayId}`);
    const data = await response.json();
    return data.data || [];
  }

  static async getLocalMatchById(id: number): Promise<LocalMatch | null> {
    try {
      const response = await this.fetchPublic(`/LocalMatch/${id}`);
      const data = await response.json();
      return data.data || null;
    } catch (error) {
      console.error('Error fetching local match:', error);
      return null;
    }
  }

  static async createLocalMatch(request: CreateLocalMatchRequest): Promise<LocalMatch> {
    const response = await this.fetchPublic(`/LocalMatch`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
    const data = await response.json();
    return data.data;
  }

  static async updateLocalMatch(id: number, request: UpdateLocalMatchRequest): Promise<LocalMatch> {
    const response = await this.fetchPublic(`/LocalMatch/${id}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    });
    const data = await response.json();
    return data.data;
  }

  static async deleteLocalMatch(id: number): Promise<boolean> {
    try {
      await this.fetchPublic(`/LocalMatch/${id}`, {
        method: 'DELETE',
      });
      return true;
    } catch (error) {
      console.error('Error deleting local match:', error);
      return false;
    }
  }

  static async toggleLocalMatchActive(id: number): Promise<LocalMatch> {
    const response = await this.fetchPublic(`/LocalMatch/${id}/toggle-active`, {
      method: 'PATCH',
    });
    const data = await response.json();
    return data.data;
  }

  static async getLocalMatchStatuses(): Promise<LocalMatchStatusInfo[]> {
    const response = await this.fetchPublic(`/LocalMatch/statuses`);
    const data = await response.json();
    return data.data || [];
  }
}

export const localMatchdayApi = LocalMatchdayApi;
